/**
 * ============================================================================
 * VOCALIRA CORE CONVERSATION ENGINE (VCCE) - Turn Decision Engine
 * ============================================================================
 * 
 * Módulo responsável por decidir quando o turno do usuário terminou.
 * 
 * PRINCÍPIO CENTRAL:
 * A pergunta NÃO é "O usuário ficou em silêncio?"
 * A pergunta É "Já existem evidências suficientes para responder?"
 * 
 * Este módulo combina múltiplos sinais para tomar uma decisão probabilística,
 * eliminando o ponto único de falha do VAD baseado apenas em volume.
 * 
 * SPRINT 3A: Integração com VoiceDiagnostics para métricas reais de plataforma.
 * 
 * @author Vocalira Team
 * @version 2.1.0 - SPRINT 3A Alternative Mobile Signals
 */

import voiceDiagnostics from './VoiceDiagnostics';

// ============================================================================
// CONFIGURAÇÃO DE SINAIS E PESOS
// ============================================================================

/**
 * Pesos de cada sinal na decisão final.
 * A soma deve ser 1.0 para facilitar interpretação.
 * 
 * SPRINT 3A: Rebalanceados para suportar mobile onde onspeechend é inconsistente.
 * Adicionados sinais alternativos (AUDIO_END_EVENT, STT_FINAL_STABLE) que
 * funcionam em TODAS as plataformas.
 * 
 * Calibração:
 * - Desktop com todos eventos: ~0.75 (bem acima do threshold)
 * - Mobile sem onspeechend: ~0.55-0.60 (atinge threshold via sinais alternativos)
 * - Meta: >95% das conversas terminam por eventos naturais
 */
const SIGNAL_WEIGHTS = {
  // Sinais do Browser - Balanceados para Desktop + Mobile
  SPEECH_END_EVENT: 0.18,      // onspeechend (confiável desktop, inconsistente mobile)
  AUDIO_END_EVENT: 0.15,       // NOVO: onaudioend (SEMPRE emitido em todas plataformas)
  STT_FINAL_RESULT: 0.15,      // isFinal do onresult
  STT_FINAL_STABLE: 0.12,      // NOVO: Final sem novo resultado por 800ms
  
  // Sinais de Análise de Conteúdo
  SENTENCE_COMPLETE: 0.10,     // Frase termina com . ! ?
  NO_CONTINUATION: 0.05,       // Não termina com "and", "but", etc
  
  // Sinais de Tempo/Pausa
  PAUSE_AFTER_SPEECH: 0.10,    // Pausa detectada após fala
  STT_INACTIVE: 0.05,          // STT não recebe novos resultados
  
  // Sinais do VAD (complementares)
  VAD_SILENCE: 0.05,           // Volume abaixo do threshold
  
  // Fallback (último recurso, meta: <5% de uso)
  FALLBACK_TIMER: 0.05,        // Timer de 3s quando nenhum evento natural chega
};

/**
 * Thresholds de decisão.
 * Quanto maior o threshold, mais conservador (menos falsos positivos).
 */
const DECISION_THRESHOLDS = {
  // Threshold principal - quando atingido, finaliza o turno
  TURN_COMPLETE: 0.50,
  
  // Threshold alto - finalização imediata (múltiplos sinais fortes)
  HIGH_CONFIDENCE: 0.70,
  
  // Threshold para ambiente ruidoso (mais conservador)
  NOISY_ENVIRONMENT: 0.60,
};

/**
 * Timeouts de segurança (em ms).
 * Garantem que o sistema nunca trave indefinidamente.
 * 
 * SPRINT 3A: Adicionados timers para sinais alternativos mobile.
 */
const SAFETY_TIMEOUTS = {
  // Tempo máximo em LISTENING sem nenhuma atividade
  MAX_IDLE_TIME: 5000,         // 5 segundos (reduzido de 10s)
  
  // Tempo máximo de fala ativa (para falas muito longas)
  MAX_SPEECH_TIME: 60000,      // 60 segundos
  
  // Tempo após STT final sem nova atividade
  STT_FINAL_COOLDOWN: 1500,    // 1.5 segundos
  
  // Tempo após onspeechend sem nova atividade
  SPEECH_END_COOLDOWN: 800,    // 800ms
  
  // Tempo mínimo de pausa para considerar fim de fala
  MIN_PAUSE_DURATION: 300,     // 300ms
  
  // Tempo de debounce para evitar decisões muito rápidas
  DECISION_DEBOUNCE: 100,      // 100ms
  
  // Tempo de inatividade com transcript existente
  TRANSCRIPT_STALE: 3000,      // 3 segundos
  
  // SPRINT 3A: Timer de estabilidade do STT final (sinal alternativo para mobile)
  STT_STABILITY_TIME: 800,     // 800ms sem novo resultado após final = estável
  
  // SPRINT 3A: Fallback timer (ÚLTIMO RECURSO - meta: <5% de uso)
  FALLBACK_TIMER: 3000,        // 3 segundos quando nenhum evento natural chega
};

/**
 * Palavras que indicam continuação (não finalizar ainda).
 */
const CONTINUATION_WORDS = [
  'and', 'but', 'or', 'so', 'because', 'however', 'although',
  'e', 'mas', 'ou', 'então', 'porque', 'porém', 'embora', // Portuguese
];

/**
 * Padrões que indicam frase completa.
 */
const SENTENCE_END_PATTERNS = [
  /[.!?]$/,                    // Pontuação final
  /[.!?]["']$/,                // Pontuação antes de aspas
  /\.{3}$/,                    // Reticências
];

// ============================================================================
// CLASSE PRINCIPAL: TurnDecisionEngine
// ============================================================================

/**
 * Motor de decisão para finalização de turno.
 * 
 * Responsabilidades:
 * - Coletar sinais de múltiplas fontes
 * - Calcular score de decisão
 * - Emitir evento de finalização quando threshold atingido
 * - Registrar logs detalhados para auditoria
 */
class TurnDecisionEngine {
  constructor(options = {}) {
    // Configurações (permite override para testes)
    this.weights = { ...SIGNAL_WEIGHTS, ...options.weights };
    this.thresholds = { ...DECISION_THRESHOLDS, ...options.thresholds };
    this.timeouts = { ...SAFETY_TIMEOUTS, ...options.timeouts };
    
    // Estado dos sinais
    this.signals = this._createInitialSignals();
    
    // Timestamps para cálculos de duração
    this.timestamps = {
      turnStart: null,
      lastSpeech: null,
      lastSTTResult: null,
      lastDecision: null,
      speechEndEvent: null,
      sttFinalEvent: null,
      audioEndEvent: null,      // SPRINT 3A: timestamp do onaudioend
    };
    
    // Timers de segurança
    this.timers = {
      maxIdle: null,
      maxSpeech: null,
      sttCooldown: null,
      speechEndCooldown: null,
      decisionDebounce: null,
      transcriptStale: null,
      sttStability: null,       // SPRINT 3A: timer para STT_FINAL_STABLE
      fallbackTimer: null,      // SPRINT 3A: fallback (último recurso)
    };
    
    // Callbacks
    this.onTurnComplete = options.onTurnComplete || (() => {});
    this.onDecisionUpdate = options.onDecisionUpdate || (() => {});
    this.onLog = options.onLog || console.log;
    
    // SPRINT 3A: Detectar plataforma para métricas
    this.platform = this._detectPlatform();
    
    // Métricas para auditoria - SPRINT 3A: Expandidas para monitorar eventos naturais vs fallback
    this.metrics = {
      decisionsEvaluated: 0,
      turnsCompleted: 0,
      completionReasons: {},
      avgDecisionTime: 0,
      signalContributions: {},
      
      // SPRINT 3A: Métricas de completamento por tipo de sinal
      completedByNaturalEvents: 0,   // onspeechend, onaudioend, ou sttStable
      completedByFallback: 0,         // Fallback timer (meta: <5%)
      
      // Detalhamento de sinais naturais
      completedBySpeechEnd: 0,        // onspeechend funcionou
      completedByAudioEnd: 0,         // onaudioend foi o sinal decisivo
      completedBySTTStable: 0,        // STT stable foi o sinal decisivo
      
      // Por plataforma
      byPlatform: {
        desktop: { natural: 0, fallback: 0 },
        android: { natural: 0, fallback: 0 },
        ios: { natural: 0, fallback: 0 },
        pwa: { natural: 0, fallback: 0 },
        other: { natural: 0, fallback: 0 },
      }
    };
    
    // Estado
    this.isActive = false;
    this.environmentType = 'normal'; // 'quiet', 'normal', 'noisy'
    
    this._log('ENGINE_INITIALIZED', { weights: this.weights, thresholds: this.thresholds });
  }
  
  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================
  
  /**
   * Inicia o motor de decisão para um novo turno.
   */
  start() {
    if (this.isActive) {
      this._log('ENGINE_ALREADY_ACTIVE');
      return;
    }
    
    this.isActive = true;
    this.signals = this._createInitialSignals();
    this.timestamps.turnStart = Date.now();
    this.timestamps.lastSpeech = null;
    this.timestamps.lastSTTResult = null;
    
    // SPRINT 3A: Iniciar interação no diagnóstico
    try {
      voiceDiagnostics.startInteraction();
      voiceDiagnostics.logEvent('ENGINE_STARTED', { platform: this.platform });
    } catch (e) {
      // Diagnóstico é opcional, não deve quebrar o fluxo
    }
    
    // Iniciar timer de segurança para idle máximo
    this._startSafetyTimer('maxIdle', this.timeouts.MAX_IDLE_TIME, 'MAX_IDLE_TIMEOUT');
    
    this._log('ENGINE_STARTED', { timestamp: this.timestamps.turnStart });
  }
  
  /**
   * Para o motor de decisão.
   */
  stop() {
    if (!this.isActive) return;
    
    this.isActive = false;
    this._clearAllTimers();
    
    const duration = Date.now() - this.timestamps.turnStart;
    this._log('ENGINE_STOPPED', { duration });
  }
  
  /**
   * Reseta o motor para um novo turno (sem parar).
   */
  reset() {
    this._clearAllTimers();
    this.signals = this._createInitialSignals();
    this.timestamps = {
      turnStart: Date.now(),
      lastSpeech: null,
      lastSTTResult: null,
      lastDecision: null,
      speechEndEvent: null,
      sttFinalEvent: null,
    };
    
    if (this.isActive) {
      this._startSafetyTimer('maxIdle', this.timeouts.MAX_IDLE_TIME, 'MAX_IDLE_TIMEOUT');
    }
    
    this._log('ENGINE_RESET');
  }
  
  // ==========================================================================
  // RECEPÇÃO DE SINAIS
  // ==========================================================================
  
  /**
   * Registra detecção de voz pelo VAD.
   * @param {number} volume - Volume normalizado (0-1)
   * @param {boolean} isVoiceFrequency - Se a frequência parece voz
   */
  onVoiceDetected(volume, isVoiceFrequency) {
    if (!this.isActive) return;
    
    this.timestamps.lastSpeech = Date.now();
    this.signals.hasSpoken = true;
    this.signals.lastVolume = volume;
    this.signals.isVoiceFrequency = isVoiceFrequency;
    
    // Cancelar timer de idle e iniciar timer de fala máxima
    this._clearTimer('maxIdle');
    if (!this.timers.maxSpeech) {
      this._startSafetyTimer('maxSpeech', this.timeouts.MAX_SPEECH_TIME, 'MAX_SPEECH_TIMEOUT');
    }
    
    // Resetar cooldowns se usuário voltou a falar
    this._clearTimer('sttCooldown');
    this._clearTimer('speechEndCooldown');
    this.signals.speechEndEvent = false;
    this.signals.sttFinalReceived = false;
    
    this._log('SIGNAL_VOICE_DETECTED', { volume: volume.toFixed(4), isVoiceFrequency });
  }
  
  /**
   * Registra silêncio detectado pelo VAD.
   * @param {number} volume - Volume normalizado
   * @param {number} silenceDuration - Duração do silêncio em ms
   */
  onSilenceDetected(volume, silenceDuration) {
    if (!this.isActive) return;
    
    this.signals.lastVolume = volume;
    this.signals.silenceDuration = silenceDuration;
    
    // Só considerar silêncio significativo se já houve fala
    if (this.signals.hasSpoken && silenceDuration >= this.timeouts.MIN_PAUSE_DURATION) {
      this.signals.vadSilence = true;
      this._log('SIGNAL_VAD_SILENCE', { volume: volume.toFixed(4), silenceDuration });
      this._evaluateDecision('VAD_SILENCE');
    }
  }
  
  /**
   * Registra evento onspeechend do SpeechRecognition.
   * Este é um dos sinais mais confiáveis em desktop, mas inconsistente em mobile.
   * SPRINT 3A: Agora complementado por onaudioend e STT_FINAL_STABLE.
   */
  onSpeechEndEvent() {
    if (!this.isActive) return;
    
    this.signals.speechEndEvent = true;
    this.timestamps.speechEndEvent = Date.now();
    
    this._log('SIGNAL_SPEECH_END_EVENT', { 
      timestamp: this.timestamps.speechEndEvent,
      platform: this.platform 
    });
    
    // Cancelar fallback timer - temos evento natural
    this._clearTimer('fallbackTimer');
    
    // Iniciar cooldown - se não houver nova atividade, finalizar
    this._startCooldownTimer('speechEndCooldown', this.timeouts.SPEECH_END_COOLDOWN, 'SPEECH_END_COOLDOWN');
    
    this._evaluateDecision('SPEECH_END_EVENT');
  }
  
  /**
   * SPRINT 3A: Registra evento onaudioend do SpeechRecognition.
   * Este evento é SEMPRE emitido em todas as plataformas, incluindo mobile.
   * Serve como sinal alternativo quando onspeechend não é emitido.
   */
  onAudioEndEvent() {
    if (!this.isActive) return;
    
    this.signals.audioEndEvent = true;
    this.timestamps.audioEndEvent = Date.now();
    
    this._log('SIGNAL_AUDIO_END_EVENT', { 
      timestamp: this.timestamps.audioEndEvent,
      platform: this.platform,
      hadSpeechEnd: this.signals.speechEndEvent,
    });
    
    // Cancelar fallback timer - temos evento natural
    this._clearTimer('fallbackTimer');
    
    // Se já tínhamos speechEnd, não precisamos de cooldown adicional
    if (!this.signals.speechEndEvent) {
      // Usar cooldown mais curto que speechEnd pois já estamos no fim
      this._startCooldownTimer('audioEndCooldown', 500, 'AUDIO_END_COOLDOWN');
    }
    
    this._evaluateDecision('AUDIO_END_EVENT');
  }
  
  /**
   * Registra resultado parcial do STT.
   * @param {string} transcript - Texto parcial
   */
  onSTTInterimResult(transcript) {
    if (!this.isActive) return;
    
    this.timestamps.lastSTTResult = Date.now();
    this.signals.hasTranscript = transcript.trim().length > 0;
    this.signals.currentTranscript = transcript;
    
    // Cancelar cooldowns - usuário ainda está falando
    this._clearTimer('sttCooldown');
    this._clearTimer('speechEndCooldown');
    this._clearTimer('transcriptStale');
    this._clearTimer('sttStability');      // SPRINT 3A: Cancelar stability timer
    this._clearTimer('fallbackTimer');     // SPRINT 3A: Cancelar fallback timer
    
    // SPRINT 3A: Resetar sinais de fim (usuário voltou a falar)
    this.signals.sttStable = false;
    this.signals.audioEndEvent = false;
    this.signals.speechEndEvent = false;
    
    // Iniciar timer de "transcript stale" - se temos texto e nada mais acontecer
    if (this.signals.hasTranscript) {
      this._startCooldownTimer('transcriptStale', this.timeouts.TRANSCRIPT_STALE, 'TRANSCRIPT_STALE');
    }
    
    this._log('SIGNAL_STT_INTERIM', { length: transcript.length });
  }
  
  /**
   * Registra resultado final do STT.
   * SPRINT 3A: Inicia stability timer e fallback timer para garantir completamento.
   * @param {string} transcript - Texto final
   * @param {number} confidence - Confiança (0-1)
   */
  onSTTFinalResult(transcript, confidence = 1.0) {
    if (!this.isActive) return;
    
    const now = Date.now();
    this.timestamps.lastSTTResult = now;
    this.timestamps.sttFinalEvent = now;
    
    this.signals.sttFinalReceived = true;
    this.signals.hasTranscript = transcript.trim().length > 0;
    this.signals.currentTranscript = transcript;
    this.signals.sttConfidence = confidence;
    
    // Analisar conteúdo linguístico
    this._analyzeTranscriptContent(transcript);
    
    // SPRINT 3A: Registrar no diagnóstico
    try {
      voiceDiagnostics.logEvent('STT_RESULT', { 
        transcript: transcript.substring(0, 50),
        isFinal: true,
        confidence: confidence.toFixed(2),
      });
    } catch (e) {
      // Diagnóstico é opcional, não deve quebrar o fluxo principal
    }
    
    this._log('SIGNAL_STT_FINAL', { 
      length: transcript.length, 
      confidence: confidence.toFixed(2),
      sentenceComplete: this.signals.sentenceComplete,
      hasContinuation: this.signals.hasContinuation,
      platform: this.platform,
    });
    
    // Iniciar cooldown após resultado final
    this._startCooldownTimer('sttCooldown', this.timeouts.STT_FINAL_COOLDOWN, 'STT_FINAL_COOLDOWN');
    
    // SPRINT 3A: Iniciar stability timer (sinal alternativo para mobile)
    // Se não receber novo interim em 800ms, marca como estável
    this._startSTTStabilityTimer();
    
    // SPRINT 3A: Iniciar fallback timer (ÚLTIMO RECURSO)
    // Só dispara se nenhum evento natural chegar em 3s
    this._startFallbackTimer();
    
    this._evaluateDecision('STT_FINAL_RESULT');
  }
  
  /**
   * Atualiza informação sobre o ambiente.
   * @param {'quiet'|'normal'|'noisy'} type - Tipo de ambiente
   */
  setEnvironmentType(type) {
    this.environmentType = type;
    this._log('ENVIRONMENT_UPDATED', { type });
  }
  
  // ==========================================================================
  // MOTOR DE DECISÃO
  // ==========================================================================
  
  /**
   * Avalia se há evidências suficientes para finalizar o turno.
   * @param {string} trigger - O que disparou esta avaliação
   */
  _evaluateDecision(trigger) {
    if (!this.isActive) return;
    
    // Debounce para evitar avaliações muito frequentes
    if (this.timers.decisionDebounce) return;
    
    this.timers.decisionDebounce = setTimeout(() => {
      this.timers.decisionDebounce = null;
    }, this.timeouts.DECISION_DEBOUNCE);
    
    const now = Date.now();
    this.metrics.decisionsEvaluated++;
    
    // Calcular score de cada sinal
    const scores = this._calculateSignalScores();
    
    // Calcular score total
    const totalScore = Object.values(scores).reduce((sum, s) => sum + s, 0);
    
    // Determinar threshold baseado no ambiente
    const threshold = this._getThresholdForEnvironment();
    
    // Decisão
    const shouldComplete = totalScore >= threshold;
    
    // Log detalhado
    this._log('DECISION_EVALUATED', {
      trigger,
      scores,
      totalScore: totalScore.toFixed(3),
      threshold: threshold.toFixed(3),
      decision: shouldComplete ? 'COMPLETE' : 'WAIT',
      signals: { ...this.signals }
    });
    
    // Atualizar callback de progresso
    this.onDecisionUpdate({
      score: totalScore,
      threshold,
      signals: scores,
      willComplete: shouldComplete
    });
    
    // Se atingiu threshold, finalizar turno
    if (shouldComplete) {
      this._completeTurn(trigger, scores, totalScore);
    }
  }
  
  /**
   * Calcula o score de contribuição de cada sinal.
   * SPRINT 3A: Adicionados sinais AUDIO_END_EVENT, STT_FINAL_STABLE e FALLBACK_TIMER.
   * @returns {Object} Scores por sinal
   */
  _calculateSignalScores() {
    const scores = {};
    
    // 1. Speech End Event (browser detectou fim de fala - inconsistente em mobile)
    scores.speechEndEvent = this.signals.speechEndEvent 
      ? this.weights.SPEECH_END_EVENT 
      : 0;
    
    // SPRINT 3A: 2. Audio End Event (SEMPRE emitido em todas plataformas)
    scores.audioEndEvent = this.signals.audioEndEvent 
      ? this.weights.AUDIO_END_EVENT 
      : 0;
    
    // 3. STT Final Result (reconhecimento finalizado)
    scores.sttFinalResult = this.signals.sttFinalReceived 
      ? this.weights.STT_FINAL_RESULT 
      : 0;
    
    // SPRINT 3A: 4. STT Final Stable (final sem novo resultado por 800ms)
    scores.sttFinalStable = this.signals.sttStable 
      ? this.weights.STT_FINAL_STABLE 
      : 0;
    
    // 5. Sentence Complete (análise linguística)
    scores.sentenceComplete = this.signals.sentenceComplete 
      ? this.weights.SENTENCE_COMPLETE 
      : 0;
    
    // 6. No Continuation Words
    scores.noContinuation = (this.signals.hasTranscript && !this.signals.hasContinuation)
      ? this.weights.NO_CONTINUATION
      : 0;
    
    // 7. Pause After Speech (tempo desde última fala)
    const pauseDuration = this.timestamps.lastSpeech 
      ? Date.now() - this.timestamps.lastSpeech 
      : 0;
    const pauseScore = Math.min(pauseDuration / 1500, 1); // Normalizar para 0-1
    scores.pauseAfterSpeech = this.signals.hasSpoken 
      ? this.weights.PAUSE_AFTER_SPEECH * pauseScore 
      : 0;
    
    // 8. STT Inactive (tempo desde último resultado)
    const sttInactiveDuration = this.timestamps.lastSTTResult
      ? Date.now() - this.timestamps.lastSTTResult
      : 0;
    const sttInactiveScore = Math.min(sttInactiveDuration / 2000, 1);
    scores.sttInactive = (this.signals.hasTranscript && sttInactiveDuration > 500)
      ? this.weights.STT_INACTIVE * sttInactiveScore
      : 0;
    
    // 9. VAD Silence
    scores.vadSilence = this.signals.vadSilence 
      ? this.weights.VAD_SILENCE 
      : 0;
    
    // SPRINT 3A: 10. Fallback Timer (ÚLTIMO RECURSO - só conta se ativado)
    scores.fallbackTimer = this.signals.fallbackTriggered 
      ? this.weights.FALLBACK_TIMER 
      : 0;
    
    return scores;
  }
  
  /**
   * Obtém o threshold apropriado para o ambiente atual.
   * @returns {number} Threshold de decisão
   */
  _getThresholdForEnvironment() {
    switch (this.environmentType) {
      case 'quiet':
        return this.thresholds.TURN_COMPLETE * 0.9; // Mais sensível
      case 'noisy':
        return this.thresholds.NOISY_ENVIRONMENT;
      default:
        return this.thresholds.TURN_COMPLETE;
    }
  }
  
  /**
   * Finaliza o turno e emite evento.
   * SPRINT 3A: Atualiza métricas de eventos naturais vs fallback.
   * @param {string} reason - Motivo da finalização
   * @param {Object} scores - Scores que levaram à decisão
   * @param {number} totalScore - Score total
   */
  _completeTurn(reason, scores, totalScore) {
    if (!this.isActive) return;
    
    const duration = Date.now() - this.timestamps.turnStart;
    
    // Atualizar métricas básicas
    this.metrics.turnsCompleted++;
    this.metrics.completionReasons[reason] = (this.metrics.completionReasons[reason] || 0) + 1;
    this.metrics.avgDecisionTime = (this.metrics.avgDecisionTime * (this.metrics.turnsCompleted - 1) + duration) / this.metrics.turnsCompleted;
    
    // SPRINT 3A: Rastrear tipo de completamento (natural vs fallback)
    const isFallback = reason === 'FALLBACK_TIMER' || this.signals.fallbackTriggered;
    const completionType = isFallback ? 'fallback' : 'natural';
    
    if (isFallback) {
      this.metrics.completedByFallback++;
    } else {
      this.metrics.completedByNaturalEvents++;
      
      // Detalhar qual sinal natural foi decisivo
      if (this.signals.speechEndEvent) {
        this.metrics.completedBySpeechEnd++;
      } else if (this.signals.audioEndEvent) {
        this.metrics.completedByAudioEnd++;
      } else if (this.signals.sttStable) {
        this.metrics.completedBySTTStable++;
      }
    }
    
    // Atualizar métricas por plataforma
    const platformKey = this.platform || 'other';
    if (this.metrics.byPlatform[platformKey]) {
      this.metrics.byPlatform[platformKey][completionType]++;
    }
    
    // SPRINT 3A: Registrar no diagnóstico
    try {
      voiceDiagnostics.logEvent('TURN_DECISION', {
        reason,
        scores,
        totalScore: totalScore.toFixed(3),
        endSignal: isFallback ? 'fallback' : 
                   this.signals.speechEndEvent ? 'speechEnd' :
                   this.signals.audioEndEvent ? 'audioEnd' :
                   this.signals.sttStable ? 'sttStable' : 'unknown',
        platform: this.platform,
      });
      voiceDiagnostics.logEvent('INTERACTION_END', { 
        duration,
        completionType,
      });
    } catch (e) {
      // Diagnóstico é opcional
    }
    
    // Log detalhado
    this._log('TURN_COMPLETED', {
      reason,
      scores,
      totalScore: totalScore.toFixed(3),
      duration,
      transcript: this.signals.currentTranscript?.substring(0, 50),
      completionType,
      platform: this.platform,
      naturalEventsStats: this.getCompletionStats(),
    });
    
    // Limpar timers
    this._clearAllTimers();
    
    // Emitir evento
    this.onTurnComplete({
      reason,
      transcript: this.signals.currentTranscript,
      confidence: totalScore,
      duration,
      signals: { ...this.signals },
      scores,
      completionType, // SPRINT 3A: Incluir tipo no evento
    });
    
    // Resetar para próximo turno
    this.isActive = false;
  }
  
  // ==========================================================================
  // ANÁLISE LINGUÍSTICA
  // ==========================================================================
  
  /**
   * Analisa o conteúdo do transcript para sinais linguísticos.
   * @param {string} transcript - Texto a analisar
   */
  _analyzeTranscriptContent(transcript) {
    if (!transcript || !transcript.trim()) {
      this.signals.sentenceComplete = false;
      this.signals.hasContinuation = false;
      return;
    }
    
    const text = transcript.trim().toLowerCase();
    
    // Verificar se termina com pontuação de fim de frase
    this.signals.sentenceComplete = SENTENCE_END_PATTERNS.some(pattern => 
      pattern.test(transcript.trim())
    );
    
    // Verificar se termina com palavra de continuação
    const words = text.split(/\s+/);
    const lastWord = words[words.length - 1]?.replace(/[.,!?;:]$/, '');
    this.signals.hasContinuation = CONTINUATION_WORDS.includes(lastWord);
  }
  
  // ==========================================================================
  // TIMERS DE SEGURANÇA
  // ==========================================================================
  
  /**
   * Inicia um timer de segurança.
   */
  _startSafetyTimer(name, duration, reason) {
    this._clearTimer(name);
    
    this.timers[name] = setTimeout(() => {
      this._log('SAFETY_TIMEOUT', { name, duration, reason });
      
      if (this.isActive) {
        // Forçar finalização com os sinais atuais
        const scores = this._calculateSignalScores();
        const totalScore = Object.values(scores).reduce((sum, s) => sum + s, 0);
        this._completeTurn(reason, scores, Math.max(totalScore, 0.5));
      }
      
      this.timers[name] = null;
    }, duration);
  }
  
  /**
   * Inicia um timer de cooldown (após evento, aguarda antes de decidir).
   */
  _startCooldownTimer(name, duration, reason) {
    this._clearTimer(name);
    
    this.timers[name] = setTimeout(() => {
      // Verificar isActive antes de executar para evitar race conditions
      if (!this.isActive) {
        this.timers[name] = null;
        return;
      }
      
      this._log('COOLDOWN_EXPIRED', { name, duration, reason });
      this._evaluateDecision(reason);
      this.timers[name] = null;
    }, duration);
  }
  
  /**
   * Limpa um timer específico.
   */
  _clearTimer(name) {
    if (this.timers[name]) {
      clearTimeout(this.timers[name]);
      this.timers[name] = null;
    }
  }
  
  /**
   * Limpa todos os timers.
   */
  _clearAllTimers() {
    Object.keys(this.timers).forEach(name => this._clearTimer(name));
  }
  
  // ==========================================================================
  // SPRINT 3A: NOVOS TIMERS PARA SINAIS ALTERNATIVOS
  // ==========================================================================
  
  /**
   * SPRINT 3A: Inicia o timer de estabilidade do STT.
   * Se não receber novo interim em 800ms após um final, marca como estável.
   * Este é um sinal alternativo para quando onspeechend não é emitido (mobile).
   */
  _startSTTStabilityTimer() {
    this._clearTimer('sttStability');
    
    this.timers.sttStability = setTimeout(() => {
      if (!this.isActive) {
        this.timers.sttStability = null;
        return;
      }
      
      // Só marcar como estável se:
      // 1. Temos um resultado final
      // 2. Não recebemos novo interim desde então
      // 3. Não recebemos já um evento de fim (speechEnd ou audioEnd)
      if (this.signals.sttFinalReceived && 
          !this.signals.sttStable && 
          !this._hasReceivedAnyEndEvent()) {
        
        this.signals.sttStable = true;
        this._log('SIGNAL_STT_FINAL_STABLE', { 
          platform: this.platform,
          timeSinceFinal: Date.now() - this.timestamps.sttFinalEvent,
        });
        
        // Cancelar fallback - temos evento natural
        this._clearTimer('fallbackTimer');
        
        this._evaluateDecision('STT_FINAL_STABLE');
      }
      
      this.timers.sttStability = null;
    }, this.timeouts.STT_STABILITY_TIME);
  }
  
  /**
   * SPRINT 3A: Inicia o timer de fallback (ÚLTIMO RECURSO).
   * Só dispara se:
   * 1. Temos transcript válido
   * 2. NENHUM evento natural foi recebido (speechEnd, audioEnd, sttStable)
   * Meta: <5% das conversas devem precisar deste fallback.
   */
  _startFallbackTimer() {
    this._clearTimer('fallbackTimer');
    
    this.timers.fallbackTimer = setTimeout(() => {
      if (!this.isActive) {
        this.timers.fallbackTimer = null;
        return;
      }
      
      // Só usar fallback se não recebemos NENHUM evento natural
      if (this.signals.hasTranscript && !this._hasReceivedAnyEndEvent()) {
        this.signals.fallbackTriggered = true;
        
        this._log('FALLBACK_TIMER_TRIGGERED', { 
          platform: this.platform,
          reason: 'no_natural_end_events',
          hadSpeechEnd: this.signals.speechEndEvent,
          hadAudioEnd: this.signals.audioEndEvent,
          hadSTTStable: this.signals.sttStable,
          transcript: this.signals.currentTranscript?.substring(0, 30),
        });
        
        this._evaluateDecision('FALLBACK_TIMER');
      }
      
      this.timers.fallbackTimer = null;
    }, this.timeouts.FALLBACK_TIMER);
  }
  
  /**
   * SPRINT 3A: Verifica se algum evento natural de fim foi recebido.
   * Eventos naturais: onspeechend, onaudioend, sttStable
   */
  _hasReceivedAnyEndEvent() {
    return this.signals.speechEndEvent || 
           this.signals.audioEndEvent || 
           this.signals.sttStable;
  }
  
  /**
   * SPRINT 3A: Detecta a plataforma para métricas.
   */
  _detectPlatform() {
    const ua = navigator.userAgent || '';
    const isIOS = /iPhone|iPad|iPod/i.test(ua);
    const isAndroid = /Android/i.test(ua);
    const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                  window.navigator.standalone === true;
    
    if (isPWA) return 'pwa';
    if (isIOS) return 'ios';
    if (isAndroid) return 'android';
    if (/Mobile/i.test(ua)) return 'other';
    return 'desktop';
  }
  
  // ==========================================================================
  // UTILITÁRIOS
  // ==========================================================================
  
  /**
   * Cria o estado inicial dos sinais.
   * SPRINT 3A: Adicionados sinais para eventos alternativos mobile.
   */
  _createInitialSignals() {
    return {
      // Estado de fala
      hasSpoken: false,
      lastVolume: 0,
      isVoiceFrequency: false,
      silenceDuration: 0,
      vadSilence: false,
      
      // STT
      hasTranscript: false,
      currentTranscript: '',
      sttConfidence: 0,
      sttFinalReceived: false,
      sttStable: false,           // SPRINT 3A: STT final sem novo resultado por 800ms
      
      // Browser events
      speechEndEvent: false,
      audioEndEvent: false,       // SPRINT 3A: onaudioend (sempre emitido)
      
      // Análise linguística
      sentenceComplete: false,
      hasContinuation: false,
      
      // SPRINT 3A: Fallback (último recurso)
      fallbackTriggered: false,
    };
  }
  
  /**
   * Registra log com timestamp e contexto.
   */
  _log(event, data = {}) {
    const entry = {
      timestamp: Date.now(),
      timeSinceTurnStart: this.timestamps.turnStart 
        ? Date.now() - this.timestamps.turnStart 
        : 0,
      event: `[TurnDecision] ${event}`,
      data
    };
    
    this.onLog(entry.event, entry);
  }
  
  // ==========================================================================
  // API PÚBLICA PARA MÉTRICAS
  // ==========================================================================
  
  /**
   * Retorna métricas atuais do motor.
   */
  getMetrics() {
    return { ...this.metrics };
  }
  
  /**
   * Retorna estado atual dos sinais.
   */
  getCurrentSignals() {
    return { ...this.signals };
  }
  
  /**
   * Retorna score atual sem tomar decisão.
   */
  getCurrentScore() {
    const scores = this._calculateSignalScores();
    return {
      scores,
      total: Object.values(scores).reduce((sum, s) => sum + s, 0),
      threshold: this._getThresholdForEnvironment()
    };
  }
  
  /**
   * SPRINT 3A: Retorna estatísticas de completamento (eventos naturais vs fallback).
   * Use para monitorar se meta de >95% natural está sendo atingida.
   */
  getCompletionStats() {
    const total = this.metrics.turnsCompleted;
    const natural = this.metrics.completedByNaturalEvents;
    const fallback = this.metrics.completedByFallback;
    
    return {
      total,
      natural,
      fallback,
      naturalPercentage: total > 0 ? ((natural / total) * 100).toFixed(1) : '0.0',
      fallbackPercentage: total > 0 ? ((fallback / total) * 100).toFixed(1) : '0.0',
      meetsTarget: total > 0 ? (natural / total) >= 0.95 : true, // Meta: >95% natural
      breakdown: {
        bySpeechEnd: this.metrics.completedBySpeechEnd,
        byAudioEnd: this.metrics.completedByAudioEnd,
        bySTTStable: this.metrics.completedBySTTStable,
        byFallback: this.metrics.completedByFallback,
      },
      byPlatform: this.metrics.byPlatform,
    };
  }
}

export default TurnDecisionEngine;
export { SIGNAL_WEIGHTS, DECISION_THRESHOLDS, SAFETY_TIMEOUTS };
